import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

function findChildOfType(
  children: React.ReactNode,
  type: React.ElementType
): React.ReactElement | undefined {
  let found: React.ReactElement | undefined;
  React.Children.forEach(children, (child) => {
    if (found || !React.isValidElement(child)) return;
    if (child.type === type) {
      found = child;
    } else if ((child.props as any)?.children) {
      found = findChildOfType((child.props as any).children, type);
    }
  });
  return found;
}

function extractText(node: React.ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractText).join('');
  if (React.isValidElement(node)) return extractText((node.props as any)?.children);
  return '';
}

function collectItems(
  children: React.ReactNode
): { value: string; label: string; disabled?: boolean }[] {
  const items: { value: string; label: string; disabled?: boolean }[] = [];
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === SelectItem) {
      const props = child.props as SelectItemProps;
      const label = extractText(props.children).trim();
      items.push({
        value: props.value,
        label: label || props.value,
        disabled: props.disabled,
      });
    } else if ((child.props as any)?.children) {
      items.push(...collectItems((child.props as any).children));
    }
  });
  return items;
}

const baseTriggerClass =
  'w-full h-9 px-3 pr-8 text-sm rounded-lg border border-gray-300 dark:border-gray-600 ' +
  'supports-[backdrop-filter]:backdrop-blur-sm ' +
  'text-gray-900 dark:text-white focus:border-[#103254] focus:ring-1 focus:ring-[#103254] focus:outline-none ' +
  'disabled:opacity-50 disabled:cursor-not-allowed appearance-none';

interface SelectProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  name?: string;
  children: React.ReactNode;
}

const Select = ({
  value,
  defaultValue,
  onValueChange,
  disabled,
  name,
  children,
}: SelectProps) => {
  const trigger = findChildOfType(children, SelectTrigger);
  const content = findChildOfType(children, SelectContent);
  const selectValueEl = trigger
    ? findChildOfType((trigger.props as any).children, SelectValue)
    : undefined;
  const placeholder = (selectValueEl?.props as SelectValueProps | undefined)
    ?.placeholder;
  const items = content ? collectItems((content.props as any).children) : [];
  const isControlled = value !== undefined;

  return (
    <div className="relative">
      <select
        name={name}
        disabled={disabled}
        value={isControlled ? value : undefined}
        defaultValue={!isControlled ? defaultValue : undefined}
        onChange={(e) => onValueChange?.(e.target.value)}
        className={cn(baseTriggerClass, (trigger?.props as any)?.className)}
      >
        {placeholder && (
          <option value="" disabled hidden>
            {placeholder}
          </option>
        )}
        {items.map((item) => (
          <option key={item.value} value={item.value} disabled={item.disabled}>
            {item.label}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-gray-400" />
    </div>
  );
};

interface SelectTriggerProps {
  className?: string;
  children?: React.ReactNode;
}
const SelectTrigger = (_: SelectTriggerProps) => null;

interface SelectValueProps {
  placeholder?: string;
}
const SelectValue = (_: SelectValueProps) => null;

interface SelectContentProps {
  className?: string;
  children?: React.ReactNode;
  [key: string]: any;
}
const SelectContent = (_: SelectContentProps) => null;

interface SelectItemProps {
  value: string;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
}
const SelectItem = (_: SelectItemProps) => null;

export { Select, SelectTrigger, SelectValue, SelectContent, SelectItem };